import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const AI_API_BASE = 'https://cmgrcbhs9b.execute-api.ap-south-1.amazonaws.com/api/chat';
const REQUIREMENT_API = 'https://cmgrcbhs9b.execute-api.ap-south-1.amazonaws.com/api/requirements';
const REQUIREMENT_UPLOAD_API = 'https://cmgrcbhs9b.execute-api.ap-south-1.amazonaws.com/api/requirements/uploads/presign';

const hiddenPaths = [
  '/admin',
  '/forget',
  '/reset',
  '/reset-password',
  '/create-post',
  '/edit-post',
  '/create-recruitment',
  '/edit-recruitment',
  '/view-recruitment',
  '/admin/view-post',
  '/jobs-applications',
  '/job-application-view',
  '/invite',
  '/accept-invite',
  '/invite-accept',
];

const welcome = {
  from: 'bot',
  text: 'Hi. I am the InstaTenders assistant. Ask about procurement, inventory, or careers.',
};

const emptyRequirement = {
  name: '',
  companyName: '',
  mobileNumber: '',
  email: '',
  gst: '',
  productRequired: '',
  specification: '',
  quantity: '',
  deliveryLocation: '',
  preferredBrand: '',
  additionalRequirement: '',
};

const requirementLabels = [
  ['Name', 'name'],
  ['Company', 'companyName'],
  ['Mobile', 'mobileNumber'],
  ['Email', 'email'],
  ['GST Number', 'gst'],
  ['Product / Material', 'productRequired'],
  ['Specification', 'specification'],
  ['Quantity', 'quantity'],
  ['Delivery Location', 'deliveryLocation'],
  ['Preferred Brand', 'preferredBrand'],
  ['Additional Requirement', 'additionalRequirement'],
];

function replyFromData(data) {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data !== 'object') return String(data);

  const keys = ['reply', 'response', 'answer', 'output', 'content', 'text', 'message', 'result'];
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = replyFromData(value);
      if (nested) return nested;
    }
  }

  if (Array.isArray(data.messages) && data.messages.length) {
    return replyFromData(data.messages[data.messages.length - 1]);
  }

  return '';
}

function optionsFromData(data) {
  if (!Array.isArray(data?.options)) return [];
  return data.options
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const label = item.label || item.text || item.value || item.title;
        return typeof label === 'string' ? label.trim() : '';
      }
      return '';
    })
    .filter(Boolean);
}

function isPostRequirement(value) {
  return /post\s+(your\s+)?requirement/i.test(String(value || '').trim());
}

function isEditRequirement(value) {
  return /edit\s+requirement/i.test(String(value || '').trim());
}

function isSendRequirement(value) {
  return /send\s+requirement/i.test(String(value || '').trim());
}

function isMailUs(value) {
  return /click\s+to\s+mail\s+us|mail\s+us/i.test(String(value || '').trim());
}

const CONTACT_MAIL = 'info@instatenders.in';

function openMailToUs() {
  const href = `mailto:${CONTACT_MAIL}`;
  const link = document.createElement('a');
  link.href = href;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function displayValue(value) {
  const text = String(value || '').trim();
  return text || 'Not provided';
}

function fileContentType(file) {
  if (file?.type) return file.type;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}

function requirementPayload(fields, attachmentMeta = []) {
  return {
    name: String(fields.name || '').trim(),
    companyName: String(fields.companyName || '').trim(),
    mobileNumber: String(fields.mobileNumber || '').trim(),
    email: String(fields.email || '').trim(),
    gst: String(fields.gst || '').trim(),
    quantity: String(fields.quantity || '').trim(),
    productRequired: fields.productRequired || '',
    specification: fields.specification || '',
    deliveryLocation: fields.deliveryLocation || '',
    preferredBrand: fields.preferredBrand || '',
    additionalRequirement: fields.additionalRequirement || '',
    attachments: JSON.stringify(attachmentMeta),
  };
}

async function uploadRequirementFiles(email, files = []) {
  if (!files.length) return [];

  const { data } = await axios.post(REQUIREMENT_UPLOAD_API, {
    email,
    files: files.map((file) => ({
      filename: file.name,
      content_type: fileContentType(file),
      size: file.size,
    })),
  }, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  });

  const signed = Array.isArray(data?.files) ? data.files : [];
  if (!signed.length) {
    throw new Error(data?.message || 'Could not prepare the attachments.');
  }

  const uploaded = [];
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const meta = signed[index] || {};
    const uploadUrl = meta?.upload_url || meta?.uploadUrl;
    if (!uploadUrl) {
      throw new Error(`Could not prepare ${file.name}.`);
    }

    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': meta.content_type || fileContentType(file) },
      body: file,
    });
    if (!put.ok) {
      throw new Error(`Could not upload ${file.name}.`);
    }

    uploaded.push({
      filename: meta.filename || file.name,
      content_type: meta.content_type || fileContentType(file),
      size: Number(meta.size) || file.size,
      s3_key: meta.s3_key || meta.s3Key || '',
      view_url: meta.view_url || meta.viewUrl || '',
    });
  }

  return uploaded;
}

function ChatBot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([welcome]);
  const [sending, setSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [fields, setFields] = useState(emptyRequirement);
  const [attachments, setAttachments] = useState([]);
  const [formError, setFormError] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const nameRef = useRef(null);
  const sessionRef = useRef('');
  const sendingRef = useRef(false);
  const draftRef = useRef(null);
  const hidden = hiddenPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  useEffect(() => {
    setOpen(false);
    setFormOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
    if (formOpen) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 180);
    return () => window.clearTimeout(timer);
  }, [open, messages, formOpen]);

  useEffect(() => {
    if (!formOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => nameRef.current?.focus(), 80);
    return () => {
      document.body.style.overflow = previous;
      window.clearTimeout(timer);
    };
  }, [formOpen]);

  const openRequirementForm = (useDraft = false) => {
    setOpen(true);
    setFormError('');
    if (useDraft && draftRef.current) {
      setFields({ ...emptyRequirement, ...draftRef.current.fields });
      setAttachments(draftRef.current.attachments || []);
    } else {
      setFields(emptyRequirement);
      setAttachments([]);
    }
    setFormOpen(true);
  };

  const sendMessage = async (raw) => {
    const next = raw.trim();
    if (!next || sendingRef.current) return;

    if (isPostRequirement(next)) {
      setText('');
      setMessages((current) => [...current, { from: 'user', text: next }]);
      openRequirementForm(false);
      return;
    }
    if (isEditRequirement(next)) {
      openRequirementForm(true);
      return;
    }
    if (isSendRequirement(next)) {
      sendRequirement();
      return;
    }
    if (isMailUs(next)) {
      setText('');
      setMessages((current) => [
        ...current,
        { from: 'user', text: next },
        { from: 'bot', text: `Opening your email to ${CONTACT_MAIL}.` },
      ]);
      openMailToUs();
      return;
    }

    sendingRef.current = true;
    setText('');
    setSending(true);
    setMessages((current) => [
      ...current,
      { from: 'user', text: next },
      { from: 'bot', text: 'Thinking…', pending: true },
    ]);

    try {
      const payload = { message: next };
      if (sessionRef.current) payload.session_id = sessionRef.current;

      const { data } = await axios.post(AI_API_BASE, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
      });

      if (typeof data?.session_id === 'string' && data.session_id) {
        sessionRef.current = data.session_id;
      }

      const reply = replyFromData(data) || 'I could not read a reply. Please try again.';
      const options = optionsFromData(data);
      setMessages((current) => [...current.filter((item) => !item.pending), { from: 'bot', text: reply, options }]);
    } catch {
      setMessages((current) => [
        ...current.filter((item) => !item.pending),
        { from: 'bot', text: 'Could not reach the assistant. Please try again.' },
      ]);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const handleOption = (option) => {
    if (isPostRequirement(option)) {
      setMessages((current) => [...current, { from: 'user', text: option }]);
      openRequirementForm(false);
      return;
    }
    if (isEditRequirement(option)) {
      openRequirementForm(true);
      return;
    }
    if (isSendRequirement(option)) {
      sendRequirement();
      return;
    }
    if (isMailUs(option)) {
      setMessages((current) => [
        ...current,
        { from: 'user', text: option },
        { from: 'bot', text: `Opening your email to ${CONTACT_MAIL}.` },
      ]);
      openMailToUs();
      return;
    }
    sendMessage(option);
  };

  const handleField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({
      ...current,
      [name]: name === 'gst' ? value.toUpperCase() : value,
    }));
  };

  const handleFiles = (event) => {
    const next = Array.from(event.target.files || []);
    if (!next.length) return;
    setAttachments((current) => [...current, ...next]);
    event.target.value = '';
  };

  const removeFile = (index) => {
    setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleReview = (event) => {
    event.preventDefault();
    setFormError('');
    const gst = fields.gst.trim().toUpperCase();
    if (!GST_PATTERN.test(gst)) {
      setFormError('Enter a valid 15-character GSTIN.');
      return;
    }

    const nextFields = { ...fields, gst };
    draftRef.current = { fields: nextFields, attachments: [...attachments] };
    setFormOpen(false);
    setMessages((current) => [
      ...current,
      { from: 'user', text: 'Please review my requirement before sending it.' },
      {
        from: 'bot',
        text: 'Check the details below. Edit them if needed, then send to our team.',
        summary: nextFields,
        files: attachments.map((file) => file.name),
        options: ['Edit requirement', 'Send requirement'],
      },
    ]);
  };

  const sendRequirement = async () => {
    const draft = draftRef.current;
    if (!draft || sendingRef.current) return;

    sendingRef.current = true;
    setSending(true);
    setMessages((current) => [
      ...current,
      { from: 'user', text: 'Send requirement' },
      { from: 'bot', text: 'Sending your requirement…', pending: true },
    ]);

    try {
      const files = Array.isArray(draft.attachments) ? draft.attachments : [];
      if (files.length) {
        setMessages((current) => current.map((item) => (
          item.pending ? { ...item, text: 'Uploading attachments…' } : item
        )));
      }

      const uploaded = await uploadRequirementFiles(draft.fields.email, files);
      const payload = requirementPayload(draft.fields, uploaded);
      const data = new FormData();
      Object.entries(payload).forEach(([name, value]) => {
        data.append(name, value);
      });

      const { data: result, status } = await axios.post(REQUIREMENT_API, data, { timeout: 60000 });
      if (status >= 400) {
        throw new Error(result?.detail || result?.message || 'Could not send your requirement.');
      }
      draftRef.current = null;
      const confirmation = result?.email_sent
        ? 'Your requirement is saved. Our team has been notified by email.'
        : 'Your requirement is saved. Our team will contact you shortly.';
      setMessages((current) => [
        ...current.filter((item) => !item.pending),
        { from: 'bot', text: confirmation },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => `${item.loc?.at?.(-1) || 'field'}: ${item.msg}`).join('; ')
        : detail || err.response?.data?.message || err.message || 'Could not send your requirement.';
      setMessages((current) => [
        ...current.filter((item) => !item.pending),
        { from: 'bot', text: `Unable to send your requirement: ${message}`, options: ['Edit requirement', 'Send requirement'] },
      ]);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(text);
  };

  if (hidden) return null;

  return (
    <div className={`chatbot${open ? ' is-open' : ''}`}>
      {open && (
        <section className="chatbot-panel" role="dialog" aria-labelledby="chatbot-title">
          <header className="chatbot-head">
            <div className="chatbot-head-icon" aria-hidden="true">
              <img src="/Assets/Images/chatbot.gif" alt="" />
            </div>
            <div>
              <p className="kicker kicker-plain">Assistant</p>
              <h2 id="chatbot-title">InstaTenders help</h2>
            </div>
            <button type="button" className="chatbot-close" onClick={() => setOpen(false)} aria-label="Close chat">
              ×
            </button>
          </header>
          <div className="chatbot-thread" ref={listRef}>
            {messages.map((message, index) => (
              <div key={`${message.from}-${index}`} className={`chatbot-turn is-${message.from}`}>
                {message.summary ? (
                  <div className={`chatbot-bubble is-${message.from} is-summary`}>
                    <p>{message.text}</p>
                    <dl className="chatbot-summary">
                      {requirementLabels.map(([label, key]) => (
                        <div key={key} className="chatbot-summary-row">
                          <dt>{label}</dt>
                          <dd>{displayValue(message.summary[key])}</dd>
                        </div>
                      ))}
                      <div className="chatbot-summary-row">
                        <dt>Attachments</dt>
                        <dd>{message.files?.length ? message.files.join(', ') : 'None'}</dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <p className={`chatbot-bubble is-${message.from}${message.pending ? ' is-pending' : ''}`}>
                    {message.text}
                  </p>
                )}
                {message.from === 'bot' && message.options?.length ? (
                  <div className="chatbot-options" role="group" aria-label="Suggested replies">
                    {message.options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`chatbot-option${isSendRequirement(option) ? ' is-primary' : ''}`}
                        disabled={sending}
                        onClick={() => handleOption(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <form className="chatbot-form" onSubmit={handleSubmit}>
            <label className="chatbot-field">
              <span className="chatbot-sr">Message</span>
              <input
                ref={inputRef}
                type="text"
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Type your message"
                disabled={sending}
              />
            </label>
            <button type="submit" className="btn btn-green" disabled={sending || !text.trim()}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </form>
        </section>
      )}

      {formOpen && createPortal(
        <div className="requirement-overlay">
          <section
            className="requirement-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="requirement-title"
            aria-describedby="requirement-copy"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="requirement-head">
              <div className="requirement-head-copy">
                <div className="requirement-mark" aria-hidden="true">
                  <img src="/Assets/Images/chatbot.gif" alt="" />
                </div>
                <div>
                  <p className="kicker kicker-plain">Requirement desk</p>
                  <h2 id="requirement-title">Post your requirement</h2>
                  <p id="requirement-copy">Share contact and material details. Review in chat, then send to our team.</p>
                </div>
              </div>
              <button type="button" className="requirement-close" onClick={() => setFormOpen(false)} aria-label="Close requirement form">
                ×
              </button>
            </header>
            <ol className="requirement-steps" aria-label="How this works">
              <li className="is-current">Fill details</li>
              <li>Review in chat</li>
              <li>Send to team</li>
            </ol>
            <form className="requirement-form" onSubmit={handleReview}>
              <div className="requirement-body">
                <section className="requirement-block">
                  <p className="requirement-section">Contact</p>
                  <div className="requirement-grid">
                    <label className="field">
                      <span>Name*</span>
                      <input ref={nameRef} name="name" autoComplete="name" value={fields.name} onChange={handleField} required />
                    </label>
                    <label className="field">
                      <span>Company name*</span>
                      <input name="companyName" autoComplete="organization" value={fields.companyName} onChange={handleField} required />
                    </label>
                    <label className="field">
                      <span>Mobile number*</span>
                      <input name="mobileNumber" type="tel" autoComplete="tel" inputMode="tel" value={fields.mobileNumber} onChange={handleField} required />
                    </label>
                    <label className="field">
                      <span>Email*</span>
                      <input name="email" type="email" autoComplete="email" value={fields.email} onChange={handleField} required />
                    </label>
                    <label className="field">
                      <span>GST number*</span>
                      <input
                        name="gst"
                        value={fields.gst}
                        onChange={handleField}
                        required
                        minLength={15}
                        maxLength={15}
                        placeholder="22AAAAA0000A1Z5"
                        autoCapitalize="characters"
                        spellCheck={false}
                      />
                      <em className="requirement-hint">15-character GSTIN. Last character can be a letter or digit.</em>
                    </label>
                    <label className="field">
                      <span>Quantity*</span>
                      <input name="quantity" value={fields.quantity} onChange={handleField} required />
                    </label>
                  </div>
                </section>
                <section className="requirement-block">
                  <p className="requirement-section">What you need</p>
                  <div className="requirement-grid">
                    <label className="field requirement-wide">
                      <span>Product / material required</span>
                      <input name="productRequired" value={fields.productRequired} onChange={handleField} />
                    </label>
                    <label className="field requirement-wide">
                      <span>Specification / description</span>
                      <textarea name="specification" rows="3" value={fields.specification} onChange={handleField} />
                    </label>
                    <label className="field">
                      <span>Delivery location</span>
                      <input name="deliveryLocation" value={fields.deliveryLocation} onChange={handleField} />
                    </label>
                    <label className="field">
                      <span>Preferred brand</span>
                      <input name="preferredBrand" value={fields.preferredBrand} onChange={handleField} />
                    </label>
                    <label className="field requirement-wide">
                      <span>Additional requirement</span>
                      <textarea name="additionalRequirement" rows="3" value={fields.additionalRequirement} onChange={handleField} />
                    </label>
                    <label className="field requirement-wide">
                      <span>Attachments</span>
                      <div className="requirement-upload">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.docx,.png,.jpeg,.jpg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                          onChange={handleFiles}
                        />
                        <strong>Add files</strong>
                        <em>PDF, DOCX, PNG, or JPG</em>
                      </div>
                      {attachments.length > 0 && (
                        <ul className="requirement-files">
                          {attachments.map((file, index) => (
                            <li key={`${file.name}-${index}`}>
                              <span>{file.name}</span>
                              <button type="button" onClick={() => removeFile(index)}>
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </label>
                  </div>
                </section>
              </div>
              {formError && <p className="form-error">{formError}</p>}
              <div className="requirement-actions">
                <button type="button" className="btn btn-outline" onClick={() => setFormOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-green">
                  Review in chat
                </button>
              </div>
            </form>
          </section>
        </div>,
        document.body
      )}

      <button
        type="button"
        className="chatbot-launcher"
        aria-expanded={open}
        aria-controls="chatbot-title"
        onClick={() => setOpen((current) => !current)}
      >
        <img className="chatbot-robot" src="/Assets/Images/chatbot.gif" alt="" />
        <span className="chatbot-sr">{open ? 'Close chat' : 'Open chat'}</span>
      </button>
    </div>
  );
}

export default ChatBot;
